/**
 * Combat Hook
 * 
 * Manages combat state and executes combat rounds using the bestiary system.
 */

import { useCallback } from 'react';
import { Player, Monster, CombatState } from '../types/player';
import { PartySize, CombatStats } from '../utils/combat';
import {
  rollPartyAttack,
  executeCombatRound,
  executeTwoDiceCombatRound,
  initializeMonster,
} from '../utils/combat';
import { getCreature } from '../data/bestiary';
import { usePlayer } from '../contexts/PlayerContext';

export interface UseCombatReturn {
  combatState: CombatState | null;
  startCombat: (monsterName: string, party: Player[], partySize: PartySize) => void;
  executeRound: () => Promise<CombatRoundResult>; // Now async
  flee: () => void;
  reset: () => void;
}

export interface CombatRoundResult {
  success: boolean;
  partyHpAfter: number[];
  monsterHpAfter: number;
  isMonsterDefeated: boolean;
  areAllPlayersDefeated: boolean;
  partyTotal: number;
  monsterAttack: number;
  damage: number;
  winner: 'party' | 'monster';
}

export function useCombat(): UseCombatReturn {
  const { combatState, setCombatState, saveStateImmediately } = usePlayer();

  const startCombat = useCallback(
    (monsterName: string, party: Player[], partySize: PartySize) => {
      // Guard against empty monster name
      if (!monsterName || monsterName.trim() === '') {
        console.error('❌ startCombat called with empty monster name');
        throw new Error('Failed to initialize monster: monster name is empty or undefined');
      }
      
      const monsterData = initializeMonster(monsterName, partySize);
      if (!monsterData) {
        console.error(`❌ Could not find monster "${monsterName}" in bestiary for party size ${partySize}`);
        throw new Error(`Failed to initialize monster: ${monsterName} (not found in bestiary)`);
      }

      // Get creature data for new two-dice system stats
      const creatureData = getCreature(monsterName);
      const creatureStats = creatureData?.statsByPartySize[partySize];

      const monster: Monster = {
        id: `monster-${Date.now()}`,
        name: monsterData.name,
        currentHp: monsterData.currentHp,
        maxHp: monsterData.maxHp,
        attack: monsterData.attack,
        attackDie: creatureStats?.attackDie,
        damageDie: creatureStats?.damageDie,
        defense: creatureStats?.defense || 0,
        tier: creatureData?.tier,
        imagePath: `/assets/monsters/${monsterName.toLowerCase()}.png`,
      };

      setCombatState({
        isActive: true,
        player: party[0], // Primary player
        party,
        partySize,
        monster,
        storyLines: [`You encounter a ${monsterName}!`],
        round: 0,
        canFlee: true,
      });
    },
    []
  );

  const executeRound = useCallback(async (): Promise<CombatRoundResult> => {
    const roundStartTimestamp = performance.now();
    console.log('⚔️ [INVESTIGATION] executeRound called at', new Date().toISOString());
    
    if (!combatState || !combatState.monster || !combatState.party) {
      throw new Error('Combat not initialized');
    }

    const monster = combatState.monster;
    const isSoloPlayer = combatState.party.length === 1;
    const useNewCombatSystem = isSoloPlayer && monster.attackDie && monster.damageDie;

    let result: any;
    let roundDescription: string;
    const oldRound = combatState.round;
    let newRound = combatState.round + 1;
    console.log(`   📊 [INVESTIGATION] Round increment: ${oldRound} → ${newRound} at T+${(performance.now() - roundStartTimestamp).toFixed(2)}ms`);
    let updatedMonster: Monster;
    let updatedParty: Player[];
    let newCombatState: Partial<CombatState> = {};

    if (useNewCombatSystem) {
      // NEW TWO-DICE COMBAT SYSTEM (solo player only)
      const player = combatState.party[0];
      
      const playerStats: CombatStats = {
        hp: player.hp,
        attackDie: 11,  // Player uses d11 (rebalanced Dec 2025)
        damageDie: 10,  // Player uses d10
        defense: 2,     // Player base armor
      };
      
      const monsterStats: CombatStats = {
        hp: monster.currentHp,
        attackDie: monster.attackDie!,
        damageDie: monster.damageDie!,
        defense: monster.defense || 0,  // Monster defense from bestiary
      };

      // Execute new two-dice combat round
      const twoDiceResult = executeTwoDiceCombatRound(playerStats, monsterStats);
      
      // Update HP
      const newPlayerHP = player.hp - twoDiceResult.damageToPlayer;
      const newMonsterHP = monster.currentHp - twoDiceResult.damageToMonster;
      
      updatedMonster = {
        ...monster,
        currentHp: Math.max(0, newMonsterHP),
      };
      
      updatedParty = [{
        ...player,
        hp: Math.max(0, newPlayerHP),
      }];
      
      // Format new combat description
      if (twoDiceResult.winner === 'tie') {
        roundDescription = `You roll ${twoDiceResult.playerAttackRoll} to attack. ${monster.name} rolls ${twoDiceResult.monsterAttackRoll} to attack. It's a tie! No damage dealt.`;
      } else if (twoDiceResult.winner === 'player') {
        const monsterDefense = monster.defense || 0;
        const defenseText = monsterDefense > 0 
          ? ` ${monster.name}'s ${monsterDefense} defense reduces this to ${twoDiceResult.damageToMonster} damage.`
          : '';
        roundDescription = `You roll ${twoDiceResult.playerAttackRoll} to attack. ${monster.name} rolls ${twoDiceResult.monsterAttackRoll} to attack. You win the round! Your damage roll is ${twoDiceResult.playerDamageRoll}.${defenseText} ${monster.name} takes ${twoDiceResult.damageToMonster} damage.`;
      } else {
        const playerDefense = 2;
        const defenseText = playerDefense > 0
          ? ` Your ${playerDefense} defense reduces this to ${twoDiceResult.damageToPlayer} damage.`
          : '';
        roundDescription = `You roll ${twoDiceResult.playerAttackRoll} to attack. ${monster.name} rolls ${twoDiceResult.monsterAttackRoll} to attack. ${monster.name} wins the round! ${monster.name}'s damage roll is ${twoDiceResult.monsterDamageRoll}.${defenseText} You take ${twoDiceResult.damageToPlayer} damage.`;
      }
      
      // Store new combat state fields
      newCombatState = {
        playerAttackRoll: twoDiceResult.playerAttackRoll,
        monsterAttackRoll: twoDiceResult.monsterAttackRoll,
        playerDamageRoll: twoDiceResult.playerDamageRoll,
        monsterDamageRoll: twoDiceResult.monsterDamageRoll,
        damage: Math.max(twoDiceResult.damageToPlayer, twoDiceResult.damageToMonster),
        winner: twoDiceResult.winner,
      };
      
      result = {
        isMonsterDefeated: newMonsterHP <= 0,
        areAllPlayersDefeated: newPlayerHP <= 0,
        partyHpAfter: [Math.max(0, newPlayerHP)],
        monsterHpAfter: Math.max(0, newMonsterHP),
      };
      
    } else {
      // LEGACY COMBAT SYSTEM (party or no new stats)
      const partyRolls = rollPartyAttack(combatState.party.length);
      const partyHp = combatState.party.map(p => p.hp);

      const legacyResult = executeCombatRound(
        partyRolls,
        monster.name,
        combatState.partySize,
        monster.currentHp,
        partyHp
      );

      updatedMonster = {
        ...monster,
        currentHp: legacyResult.monsterHpAfter,
      };

      updatedParty = combatState.party.map((player, index) => ({
        ...player,
        hp: legacyResult.partyHpAfter[index],
      }));

      const partyLabel = combatState.party.length === 1 ? 'The player' : 'The party';
      const loserLabel = legacyResult.round.winner === 'party' 
        ? `The ${monster.name}`
        : 'Each party member';
      
      roundDescription = legacyResult.round.damage > 0
        ? `${partyLabel} attacked with ${legacyResult.round.partyTotal}, the ${monster.name} attacked with ${legacyResult.round.monsterAttack}. ${loserLabel} takes ${legacyResult.round.damage} damage.`
        : `${partyLabel} attacked with ${legacyResult.round.partyTotal}, the ${monster.name} attacked with ${legacyResult.round.monsterAttack}. It's a tie! No damage dealt.`;

      newCombatState = {
        playerRolls: partyRolls,
        partyTotal: legacyResult.round.partyTotal,
        monsterAttack: legacyResult.round.monsterAttack,
        damage: legacyResult.round.damage,
        winner: legacyResult.round.winner,
      };
      
      result = legacyResult;
    }

    // Update combat state
    const newStoryLines = [
      ...combatState.storyLines,
      roundDescription,
    ];

    const setCombatTimestamp = performance.now();
    console.log(`   🔄 [INVESTIGATION] Calling setCombatState with round ${newRound} at T+${(setCombatTimestamp - roundStartTimestamp).toFixed(2)}ms`);
    
    const newCombatStateComplete = {
      ...combatState,
      ...newCombatState,
      round: newRound,
      monster: updatedMonster,
      party: updatedParty,
      player: updatedParty[0],
      storyLines: newStoryLines,
      isActive: !result.isMonsterDefeated && !result.areAllPlayersDefeated,
      canFlee: !result.isMonsterDefeated && !result.areAllPlayersDefeated,
    };
    
    setCombatState(newCombatStateComplete);
    
    // CRITICAL: Await Firestore save before allowing next action
    // Pass the new state directly to avoid reading stale React state
    const saveStartTimestamp = performance.now();
    console.log(`   💾 [INVESTIGATION] Awaiting immediate Firestore save at T+${(saveStartTimestamp - roundStartTimestamp).toFixed(2)}ms`);
    await saveStateImmediately({ combatState: newCombatStateComplete });
    const saveEndTimestamp = performance.now();
    console.log(`   ✅ [INVESTIGATION] Save completed, returning from executeRound at T+${(saveEndTimestamp - roundStartTimestamp).toFixed(2)}ms (total including save)`);

    return {
      success: true,
      partyHpAfter: result.partyHpAfter,
      monsterHpAfter: result.monsterHpAfter,
      isMonsterDefeated: result.isMonsterDefeated,
      areAllPlayersDefeated: result.areAllPlayersDefeated,
      partyTotal: newCombatState.partyTotal || 0,
      monsterAttack: newCombatState.monsterAttack || 0,
      damage: newCombatState.damage || 0,
      winner: (newCombatState.winner === 'player' ? 'party' : newCombatState.winner) as 'party' | 'monster',
    };
  }, [combatState, setCombatState, saveStateImmediately]);

  const flee = useCallback(() => {
    if (!combatState) return;

    setCombatState({
      ...combatState,
      isActive: false,
      canFlee: false,
      storyLines: [
        ...combatState.storyLines,
        'The party flees from combat!',
      ],
    });
  }, [combatState, setCombatState]);

  const reset = useCallback(() => {
    setCombatState(null);
  }, [setCombatState]);

  return {
    combatState,
    startCombat,
    executeRound,
    flee,
    reset,
  };
}

